import React, {useMemo} from 'react';
import {alpha, Box, ButtonBase, Stack, Typography} from '@mui/material';
import {red} from '@mui/material/colors';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import {useQuery} from '@tanstack/react-query';
import {StyleSx} from 'app/types/common';
import {useCloud} from 'app/components/features/cloud';
import {MainRoutes, router} from 'app/router/main';
import {useTeamBilling} from 'app/components/BillingManager/hooks/useTeamBilling';
import {apiClient} from 'app/components/shared';
import {pluralizeN} from 'app/components/helpers/commonHelpers';

interface Props {
  routeName: MainRoutes;
}

export default function DelinquencyBanner({routeName}: Props) {
  const {team} = useCloud();

  const {data: delinquency} = useDelinquencyQuery(team.id);

  if (!delinquency || !delinquency.delinquent) {
    return null;
  }

  return <Inner delinquency={delinquency} routeName={routeName} />;
}

type State =
  | {enabled: boolean; status: 'ok' | 'trialing' | 'failed_payment'}
  | {enabled: false; status: 'delinquent' | 'no_subscription'};

interface Delinquency {
  delinquent: boolean;
  corrupted: boolean;
  edge: State;
  unify: State;
  connect: State;
  files: State;
  payg: State;
}

function useDelinquencyQuery(teamId: string) {
  return useQuery({
    queryKey: ['delinquency', teamId],
    queryFn: async () =>
      apiClient.get<Delinquency>(
        `/front/api/v1t/team/${teamId}/teams/${teamId}/billing/v2/products`,
      ),
  });
}

const warningSx: StyleSx = {bgcolor: '#FA5252', color: 'white'};
const delinquentSx: StyleSx = {bgcolor: alpha('#FA5252', 0.1)};
const containerSx: StyleSx = {width: 1032, pl: 2.5, py: 1};

const buttonSx: StyleSx = {
  bgcolor: 'white',
  border: 1,
  borderColor: 'gray',
  color: 'black',
  borderRadius: 1,
  py: 0.75,
  px: 2,
  alignSelf: 'center',
};

interface InnerProps {
  delinquency: Delinquency;
  routeName: MainRoutes;
}

function Inner({delinquency, routeName}: InnerProps) {
  const {user} = useCloud();

  const features = useDelinquentFeatures(delinquency);

  const paymentAccess = user.role.canEditBilling();
  const {delinquent} = delinquency;

  return (
    <Box sx={delinquent ? delinquentSx : warningSx}>
      <Stack sx={containerSx} direction="row" alignItems="flex-start" gap={1.5}>
        {delinquent ? <LockOutlinedIcon sx={{color: '#FA5252'}} /> : <ErrorOutlineOutlinedIcon />}
        <Box flex={1}>
          <Title disabled={features.disabled} unpaid={features.unpaid} />
          <Description delinquent={features.disabled.length > 0} paymentAccess={paymentAccess} />
        </Box>

        {paymentAccess && <RedirectButton routeName={routeName} />}
      </Stack>
    </Box>
  );
}

const list = ['edge', 'unify', 'connect', 'file_management', 'payg'] as const;
type Feature = typeof list[number];

function useDelinquentFeatures(delinquency: Delinquency) {
  return useMemo(
    () => ({
      unpaid: list.filter((i) => delinquency[i].status === 'failed_payment'),
      disabled: list.filter((i) => delinquency[i].status === 'delinquent'),
    }),
    [delinquency],
  );
}

interface RedirectButtonProps {
  routeName: MainRoutes;
}

function RedirectButton({routeName}: RedirectButtonProps) {
  const {actions} = useTeamBilling();

  const handleRedirect = () => {
    if (routeName === 'settingBillingUsage') {
      actions.managePayment();
      return;
    }

    router.go('settingBillingUsage');
    setTimeout(() => {
      actions.managePayment();
    }, 100);
  };

  return (
    <ButtonBase data-id="payment-redirect-button" sx={buttonSx} onClick={handleRedirect}>
      Update payment
    </ButtonBase>
  );
}

interface TitleProps {
  disabled: Feature[];
  unpaid: Feature[];
}

function Title({unpaid, disabled}: TitleProps) {
  const delinquent = disabled.length > 0;

  const formatTitle = () => {
    if (delinquent) {
      if (disabled.length === 0) {
        return 'Features disabled';
      }

      return `${pluralizeN('Feature', disabled.length)} disabled: ${disabled
        .map(formatFeatureName)
        .join(', ')}`;
    }

    if (unpaid.length === 0) {
      return 'Payment failed';
    }

    return `Payment failed for: ${unpaid.map(formatFeatureName).join(', ')}`;
  };

  return (
    <Typography
      fontWeight={600}
      mb={0.5}
      lineHeight={1.3125}
      fontSize={16}
      color={delinquent ? red[500] : 'inherit'}
    >
      {formatTitle()}
    </Typography>
  );
}

function formatFeatureName(feature: Feature) {
  switch (feature) {
    case 'edge':
      return 'Epiphan Edge';
    case 'unify':
      return 'Epiphan Unify';
    case 'connect':
      return 'Epiphan Connect';
    case 'file_management':
      return 'Epiphan File Management';
    case 'payg':
      return 'Transcribing';
    default:
      return feature;
  }
}

interface DescriptionProps {
  delinquent: boolean;
  paymentAccess: boolean;
}

function Description({delinquent, paymentAccess}: DescriptionProps) {
  const formatDescription = () => {
    if (delinquent) {
      return (
        <>
          Paid features have been disabled because of an issue with payment.{' '}
          {paymentAccess ? (
            <>Please update your payment method to restore access</>
          ) : (
            <>
              Please contact your Epiphan Cloud team owner to update billing information in order to
              regain access.
            </>
          )}
        </>
      );
    }

    return (
      <>
        The payment for your paid features failed.{' '}
        {paymentAccess
          ? 'Please update your payment method'
          : 'Contact your Epiphan cloud team owner'}{' '}
        to ensure uninterrupted service.
      </>
    );
  };
  return <Typography>{formatDescription()}</Typography>;
}
